"use client";

import { create } from "zustand";
import {
    DEMO_STORY,
    DEMO_CHAPTERS,
    DEMO_LORE,
    DEMO_LORE_CATEGORIES,
    DEMO_LORE_CONNECTIONS,
    type DemoChapterSeed,
    type DemoLoreSeed,
    type DemoLoreCategorySeed,
    type DemoLoreConnectionSeed,
    type DemoLoreCategory,
} from "@/lib/demo/demo-seed";
import {
    revokeDemoImageUrl,
    revokeDemoImageUrls,
} from "@/lib/demo/demo-image-urls";

export type DemoChapter = DemoChapterSeed;
export type DemoLore = DemoLoreSeed;

interface DemoState {
    storyId: string;
    storyTitle: string;
    chapters: DemoChapter[];
    lore: DemoLore[];
    loreCategories: DemoLoreCategorySeed[];
    loreConnections: DemoLoreConnectionSeed[];
    /** id of the chapter currently open in the editor. */
    activeChapterId: string;
    /** Latest Cloudflare Turnstile token (null until solved / if disabled). */
    turnstileToken: string | null;

    // ── actions (also called from demoEditorBackend via getState()) ──
    setActiveChapter: (id: string) => void;
    setTurnstileToken: (token: string | null) => void;
    saveChapter: (
        chapterId: string,
        content: Record<string, unknown>,
        wordCount: number,
    ) => void;
    setStoryTitle: (title: string) => void;
    addChapter: (title?: string) => string;
    setChapterStatus: (
        chapterId: string,
        status: "draft" | "published",
    ) => void;
    setChapterMeta: (
        chapterId: string,
        updates: {
            title?: string;
            authorNote?: Record<string, unknown> | null;
        },
    ) => void;
    reorderChapters: (orderedIds: string[]) => void;
    searchWiki: (query: string) => DemoLore[];
    addLoreCategory: (category: DemoLoreCategory, label: string) => string;
    updateLoreCategory: (id: string, label: string) => void;
    deleteLoreCategory: (id: string) => void;
    addLoreEntry: (
        categoryId: string,
        name: string,
        parentEntryId?: string | null,
        idOverride?: string,
    ) => string;
    updateLoreEntry: (
        id: string,
        updates: Partial<
            Pick<
                DemoLore,
                | "name"
                | "description"
                | "content"
                | "imageUrl"
                | "categoryId"
            >
        >,
    ) => void;
    deleteLoreEntry: (id: string) => void;
    addLoreConnection: (
        sourceEntryId: string,
        targetEntryId: string,
        relationLabel: string,
        idOverride?: string,
    ) => string;
    deleteLoreConnection: (id: string) => void;
    /** Restore the pristine seed. A browser refresh naturally does the same. */
    reset: () => void;
}

function seedChapters(): DemoChapter[] {
    // Deep clone so edits never mutate the shared seed objects.
    return DEMO_CHAPTERS.map((c) => ({
        ...c,
        content: structuredClone(c.content),
    }));
}

function seedLore(): DemoLore[] {
    return DEMO_LORE.map((entry) => ({
        ...entry,
        content: entry.content ? structuredClone(entry.content) : null,
    }));
}

function makeId(prefix: string): string {
    return `${prefix}-${crypto.randomUUID()}`;
}

export const useDemoStore = create<DemoState>()((set, get) => ({
    storyId: DEMO_STORY.id,
    storyTitle: DEMO_STORY.title,
    chapters: seedChapters(),
    lore: seedLore(),
    loreCategories: DEMO_LORE_CATEGORIES.map((category) => ({ ...category })),
    loreConnections: DEMO_LORE_CONNECTIONS.map((connection) => ({
        ...connection,
    })),
    activeChapterId: DEMO_CHAPTERS[0].id,
    turnstileToken: null,

    setActiveChapter: (id) => set({ activeChapterId: id }),
    setTurnstileToken: (token) => set({ turnstileToken: token }),
    setStoryTitle: (title) =>
        set({ storyTitle: title.trim() || DEMO_STORY.title }),

    saveChapter: (chapterId, content, wordCount) =>
        set((s) => ({
            chapters: s.chapters.map((c) =>
                c.id === chapterId ? { ...c, content, wordCount } : c,
            ),
        })),

    addChapter: (title) => {
        const id = `demo-ch-${Date.now()}`;
        set((s) => {
            const nextOrder =
                Math.max(0, ...s.chapters.map((c) => c.sortOrder)) + 1;
            const chapter: DemoChapter = {
                id,
                title: title?.trim() || `Chapter ${nextOrder}`,
                sortOrder: nextOrder,
                status: "draft",
                wordCount: 0,
                content: {
                    type: "doc",
                    content: [{ type: "paragraph" }],
                },
            };
            return {
                chapters: [...s.chapters, chapter],
                activeChapterId: id,
            };
        });
        return id;
    },

    setChapterStatus: (chapterId, status) =>
        set((s) => ({
            chapters: s.chapters.map((c) =>
                c.id === chapterId ? { ...c, status } : c,
            ),
        })),

    setChapterMeta: (chapterId, updates) =>
        set((s) => ({
            chapters: s.chapters.map((c) =>
                c.id === chapterId
                    ? {
                          ...c,
                          ...(updates.title !== undefined && {
                              title: updates.title,
                          }),
                          ...(updates.authorNote !== undefined && {
                              authorNote: updates.authorNote,
                          }),
                      }
                    : c,
            ),
        })),

    reorderChapters: (orderedIds) =>
        set((s) => {
            const byId = new Map(s.chapters.map((c) => [c.id, c]));
            const reordered = orderedIds
                .map((id) => byId.get(id))
                .filter((c): c is DemoChapter => Boolean(c))
                .map((c, idx) => ({ ...c, sortOrder: idx + 1 }));
            // Guard against a partial id list dropping chapters.
            return reordered.length === s.chapters.length
                ? { chapters: reordered }
                : {};
        }),

    searchWiki: (query) => {
        const q = query.trim().toLowerCase();
        const all = get().lore;
        if (!q) return all;
        return all.filter(
            (l) =>
                l.name.toLowerCase().includes(q) ||
                l.description.toLowerCase().includes(q),
        );
    },

    addLoreCategory: (category, label) => {
        const id = makeId("demo-category");
        set((state) => ({
            loreCategories: [
                ...state.loreCategories,
                {
                    id,
                    category,
                    label: label.trim() || "Other",
                },
            ],
        }));
        return id;
    },

    updateLoreCategory: (id, label) => {
        const trimmed = label.trim();
        if (!trimmed) return;
        set((state) => ({
            loreCategories: state.loreCategories.map((category) =>
                category.id === id ? { ...category, label: trimmed } : category,
            ),
            lore: state.lore.map((entry) =>
                entry.categoryId === id
                    ? { ...entry, categoryLabel: trimmed }
                    : entry,
            ),
        }));
    },

    deleteLoreCategory: (id) => {
        const removedEntries = get().lore.filter(
            (entry) => entry.categoryId === id,
        );
        removedEntries.forEach((entry) => revokeDemoImageUrl(entry.imageUrl));
        set((state) => {
            const removedIds = new Set(removedEntries.map((entry) => entry.id));
            return {
                loreCategories: state.loreCategories.filter(
                    (category) => category.id !== id,
                ),
                lore: state.lore.filter((entry) => entry.categoryId !== id),
                loreConnections: state.loreConnections.filter(
                    (connection) =>
                        !removedIds.has(connection.sourceEntryId) &&
                        !removedIds.has(connection.targetEntryId),
                ),
            };
        });
    },

    addLoreEntry: (categoryId, name, parentEntryId = null, idOverride) => {
        const category = get().loreCategories.find(
            (candidate) => candidate.id === categoryId,
        );
        if (!category) return "";
        const id = idOverride ?? makeId("demo-lore");
        set((state) => ({
            lore: [
                ...state.lore,
                {
                    id,
                    name: name.trim() || "Untitled entry",
                    category: category.category,
                    categoryLabel: category.label,
                    categoryId,
                    parentEntryId,
                    description: "",
                    content: null,
                    imageUrl: null,
                },
            ],
        }));
        return id;
    },

    updateLoreEntry: (id, updates) => {
        const current = get().lore.find((entry) => entry.id === id);
        if (
            updates.imageUrl !== undefined &&
            current?.imageUrl !== updates.imageUrl
        ) {
            revokeDemoImageUrl(current?.imageUrl);
        }
        set((state) => ({
            lore: state.lore.map((entry) => {
                const isMovedEntry = entry.id === id;
                const isChildOfMovedEntry =
                    updates.categoryId !== undefined &&
                    entry.parentEntryId === id;
                if (!isMovedEntry && !isChildOfMovedEntry) return entry;
                const category = updates.categoryId
                    ? state.loreCategories.find(
                          (candidate) => candidate.id === updates.categoryId,
                      )
                    : undefined;
                return {
                    ...entry,
                    ...(isMovedEntry
                        ? updates
                        : { categoryId: updates.categoryId! }),
                    ...(category && {
                        category: category.category,
                        categoryLabel: category.label,
                    }),
                };
            }),
        }));
    },

    deleteLoreEntry: (id) => {
        const existing = get().lore;
        const removedIds = new Set([id]);
        let changed = true;
        while (changed) {
            changed = false;
            for (const entry of existing) {
                if (
                    entry.parentEntryId &&
                    removedIds.has(entry.parentEntryId) &&
                    !removedIds.has(entry.id)
                ) {
                    removedIds.add(entry.id);
                    changed = true;
                }
            }
        }
        existing
            .filter((entry) => removedIds.has(entry.id))
            .forEach((entry) => revokeDemoImageUrl(entry.imageUrl));
        set((state) => {
            return {
                lore: state.lore.filter((entry) => !removedIds.has(entry.id)),
                loreConnections: state.loreConnections.filter(
                    (connection) =>
                        !removedIds.has(connection.sourceEntryId) &&
                        !removedIds.has(connection.targetEntryId),
                ),
            };
        });
    },

    addLoreConnection: (
        sourceEntryId,
        targetEntryId,
        relationLabel,
        idOverride,
    ) => {
        const entries = get().lore;
        if (
            sourceEntryId === targetEntryId ||
            !entries.some((entry) => entry.id === sourceEntryId) ||
            !entries.some((entry) => entry.id === targetEntryId)
        ) {
            return "";
        }
        const id = idOverride ?? makeId("demo-connection");
        set((state) => ({
            loreConnections: [
                ...state.loreConnections,
                { id, sourceEntryId, targetEntryId, relationLabel },
            ],
        }));
        return id;
    },

    deleteLoreConnection: (id) =>
        set((state) => ({
            loreConnections: state.loreConnections.filter(
                (connection) => connection.id !== id,
            ),
        })),

    reset: () => {
        // Release any in-memory demo image blobs before reseeding.
        revokeDemoImageUrls();
        set({
            storyTitle: DEMO_STORY.title,
            chapters: seedChapters(),
            lore: seedLore(),
            loreCategories: DEMO_LORE_CATEGORIES.map((category) => ({
                ...category,
            })),
            loreConnections: DEMO_LORE_CONNECTIONS.map((connection) => ({
                ...connection,
            })),
            activeChapterId: DEMO_CHAPTERS[0].id,
        });
    },
}));
